'use client';

import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';

interface MarketingSearchBarProps {
  placeholder: string;
  buttonText: string;
  searchUrl: string;
  className?: string;
  background?: string;
  titleColor?: string;
  footer?: React.ReactNode;
}

const MarketingSearchBar: React.FC<MarketingSearchBarProps> = ({
  placeholder,
  buttonText,
  searchUrl,
  className = '',
  background = 'bg-secondary',
  titleColor = 'text-primary',
  footer,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const router = useRouter();

  useEffect(() => {
    const placeholders = [
      'Breast Cancer',
      'Basal Cell Skin Cancer',
      'Melanoma',
      'Leukemia',
      'Lymphoma',
      'Ovarian Cancer',
      'Pancreatic Cancer',
    ];

    let currentIndex = 0;
    let currentChar = 0;
    let isDeleting = false;
    let timeoutId: NodeJS.Timeout;

    const animatePlaceholder = () => {
      const currentText = placeholders[currentIndex];

      if (isDeleting) {
        setCurrentPlaceholder(currentText.substring(0, currentChar));
        currentChar--;

        if (currentChar === 0) {
          isDeleting = false;
          currentIndex = (currentIndex + 1) % placeholders.length;
        }
      } else {
        setCurrentPlaceholder(currentText.substring(0, currentChar));
        currentChar++;

        if (currentChar > currentText.length) {
          isDeleting = true;
          timeoutId = setTimeout(animatePlaceholder, 3000);
          return;
        }
      }

      const speed = isDeleting ? 50 : 100;
      timeoutId = setTimeout(animatePlaceholder, speed);
    };

    animatePlaceholder();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const encodedQuery = encodeURIComponent(searchQuery);
    const fullUrl = `${searchUrl}?query=${encodedQuery}`;
    router.push(fullUrl);
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  return (
    <div
      className={`rh-searchbar px-4 sm:px-8 py-4 border-y border-primary ${background} ${className}`}
    >
      <form id="program-search-form" onSubmit={handleSubmit} className="relative w-full mx-auto">
        <div className="container mx-auto relative">
          <label htmlFor="searchbar-input" className="sr-only">
            search query
          </label>
          <input
            id="searchbar-input"
            name="query"
            className={`rh-searchbar-input ${titleColor}`}
            value={searchQuery}
            onChange={handleChange}
            placeholder={currentPlaceholder || placeholder}
            autoComplete="off"
            inputMode="search"
            aria-label="Search for a condition"
          />
          <button type="submit" className="rh-searchbar-btn">
            {buttonText}
          </button>
        </div>
      </form>
      {footer && <div className="text-center mt-6">{footer}</div>}
    </div>
  );
};

export default MarketingSearchBar;
